import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import '../styles/ScholarOTP.css';
import { postRequest } from '../utilities/apicalls';
import { Button } from "antd";
import ToastMessage from '../components/ToastMessage';

/**
 * ScholarOTP Component
 * 
 * Provides OTP verification functionality for scholars
 * with automatic input focus and validation.
 */
const ScholarOTP = () => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [timer, setTimer] = useState(180);
    const [isResendDisabled, setIsResendDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    useEffect(() => {
        let interval = null;
        if (timer > 0 && isResendDisabled) {
            interval = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1);
            }, 1000);
        } else if (timer === 0) {
            setIsResendDisabled(false);
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [timer, isResendDisabled]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleInputChange = (index, value) => {
        if (value.length > 1) return;
        
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) {
                prevInput.focus();
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6).split('');
        const newOtp = [...otp];
        pastedData.forEach((value, index) => {
            if (index < 6) newOtp[index] = value;
        });
        setOtp(newOtp);
    };

    const handleVerifyClick = async () => {
        const otpValue = otp.join('');
        if (otpValue.length !== 6) {
            ToastMessage.error('Please enter a complete OTP');
            return;
        }

        setLoading(true);
        
        // Create an abort controller to cancel requests if needed
        const abortController = new AbortController();
        
        try {
            // Create FormData object
            const formData = new FormData();
            formData.append('otp', otpValue);
            
            // Use FormData for the API call
            const response = await postRequest('scholars/verify_otp', formData, true, true);
            
            if (response.status === 200) {
                // Extract all data from the response
                const { 
                    id, 
                    first_name, 
                    last_name, 
                    email,
                    middle_name, 
                    suffix,
                    gender,
                    course,
                    scholar_type,
                    ...otherData 
                } = response.data.data;
                
                // Store authentication data
                localStorage.setItem('id', id);
                localStorage.setItem('scholar_id', id);
                
                // Store personal information
                localStorage.setItem('first_name', first_name || '');
                localStorage.setItem('last_name', last_name || '');
                localStorage.setItem('email', email || '');
                localStorage.setItem('middle_name', middle_name || '');
                localStorage.setItem('suffix', suffix || '');
                localStorage.setItem('gender', gender || '');
                
                // Store academic information
                localStorage.setItem('course', course || '');
                localStorage.setItem('scholarType', scholar_type || 'College');
                
                // Store any additional data that might be useful
                if (otherData) {
                    Object.keys(otherData).forEach(key => {
                        if (otherData[key] !== null && otherData[key] !== undefined) {
                            localStorage.setItem(key, otherData[key].toString());
                        }
                    });
                }
                
                // Dispatch an event to notify components that user data has changed
                window.dispatchEvent(new Event('userDataChanged'));

                ToastMessage.success('OTP verified successfully!', 1);

                // Use a variable to track if we should continue with navigation
                let shouldNavigate = true;
                
                // Navigation timeout with cleanup
                const navigationTimeout = setTimeout(() => {
                    if (shouldNavigate) {
                        history.push('/scholar-acknowledgement-receipt');
                    }
                }, 1000);
                
                // Cleanup function to prevent navigation after unmount
                return () => {
                    shouldNavigate = false;
                    clearTimeout(navigationTimeout);
                    abortController.abort();
                };
            } else {
                // Handle non-200 response
                const errorMessage = response?.messages?.error_messages?.[0] || 'Please check your OTP and try again';
                ToastMessage.error(errorMessage);
            }
        } catch (error) {
            // console.error('OTP verification error:', error);
            const errorMessage = error?.messages?.error_messages?.[0] || 'OTP verification failed. Please try again.';
            ToastMessage.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleResendClick = async () => {
        if (!isResendDisabled) {
            try {
                const email = localStorage.getItem('email');
                
                // Create FormData object
                const formData = new FormData();
                formData.append('email', email);
                
                // Use FormData for the API call
                const response = await postRequest('scholars/resend_otp', formData, true, true);
                
                if (response.status === 200) {
                    ToastMessage.success('OTP has been resent to your email');
                    setOtp(['', '', '', '', '', '']); // Clear OTP inputs
                    setTimer(180); // Reset timer to 3 minutes
                    setIsResendDisabled(true); // Disable resend button
                } else {
                    // Handle non-200 response
                    // console.error('Resend OTP error:', error);
                    const errorMessage = response?.messages?.error_messages?.[0] || 'Failed to resend OTP. Please try again.';
                    ToastMessage.error(errorMessage);
                }
            } catch (error) {
                // console.error('Resend OTP error:', error);
                const errorMessage = error?.messages?.error_messages?.[0] || 'Failed to resend OTP. Please try again.';
                ToastMessage.error(errorMessage);
            }
        }
    };

    const isOtpComplete = otp.every(digit => digit !== '');

    return (
        <div className="scholar-otp">
            <main className="body">
                <div className="container">
                    <div className="card">
                        {/* Header Section */}
                        <header className="header">
                            <img 
                                className="logo" 
                                src={`${process.env.PUBLIC_URL}/images/Smart Forms Logo.png`} 
                                alt="Smart Forms Logo" 
                            />
                            <h2 className="title">OTP Verification</h2>
                            <p className="subtitle">
                                Enter the OTP sent to your email
                            </p>
                        </header>

                        {/* OTP Input Section */}
                        <div className="otp-section">
                            <div className="otp-container">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        id={`otp-${index}`}
                                        type="text"
                                        maxLength="1"
                                        value={digit}
                                        onChange={(e) => handleInputChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={handlePaste}
                                        className="otp-input"
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Verify Button */}
                        <Button 
                            className="verify-button"
                            type="primary"
                            block
                            onClick={handleVerifyClick}
                            loading={loading}
                            disabled={!isOtpComplete}
                        >
                            {loading ? 'Verifying...' : 'Verify OTP'}
                        </Button>

                        {/* Resend Section */}
                        <div className="resend-section">
                            <Button 
                                className="resend-button"
                                type="link"
                                onClick={handleResendClick}
                                disabled={isResendDisabled}
                            >
                                {isResendDisabled 
                                    ? `Resend OTP in ${formatTime(timer)}`
                                    : 'Resend OTP'
                                }
                            </Button>
                        </div>
                    </div>

                    {/* Company Title */}
                    <div className="company-info">
                        <img 
                            className="myt-logo" 
                            src={`${process.env.PUBLIC_URL}/images/MYT Logo.png`} 
                            alt="MYT Logo" 
                        />
                        <span className="company-title">MYT Softdev Solutions Inc.</span>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ScholarOTP; 